import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'
import type { NewsType } from '@/stores/useNewsAdminStore'
import { withApiState } from '@/utils/withApiState'

export interface NewsFeedItem {
  uuid: string
  type: NewsType
  title: string
  body: string // markdown
  publishedAt: string
  readAt: string | null
}

interface FeedResponse {
  items: NewsFeedItem[]
  total: number
}

const PAGE_SIZE = 10

/**
 * Fil d'actualité côté utilisateur : news publiées par l'admin (changelog,
 * nouveautés, infos). L'état lu/non-lu est porté par le back (NewsRead) —
 * plus de localStorage comme l'ancien changelogStore.
 */
export const useNewsStore = defineStore('news', () => {
  const suiteStore = useSuiteStore()

  // Fil paginé (page /news)
  const items = ref<NewsFeedItem[]>([])
  const total = ref(0)
  const isLoading = ref(false)

  // Non-lus (modal "Quoi de neuf ?" + compteur)
  const unseenItems = ref<NewsFeedItem[]>([])
  const unseenCount = ref(0)
  const showNewsModal = ref(false)

  async function fetchFeed(
    opts: { offset?: number; type?: NewsType | null; append?: boolean } = {},
  ): Promise<void> {
    await withApiState(
      async () => {
        const params = new URLSearchParams()
        params.append('limit', String(PAGE_SIZE))
        params.append('offset', String(opts.offset ?? 0))
        if (opts.type) params.append('type', opts.type)

        const res = await suiteStore.suiteFetch(`news?${params.toString()}`, {
          method: 'GET',
          credentials: 'include',
        })
        if (res && typeof res !== 'boolean' && res.ok) {
          const body = await res.json()
          const data = body?.data as FeedResponse | undefined
          if (data) {
            items.value = opts.append ? [...items.value, ...data.items] : data.items
            total.value = data.total
          }
        }
      },
      { loading: isLoading, logLabel: "Erreur récupération du fil d'actualité" },
    )
  }

  async function fetchUnseen(): Promise<NewsFeedItem[]> {
    try {
      const res = await suiteStore.suiteFetch('news?unseenOnly=true&limit=50', {
        method: 'GET',
        credentials: 'include',
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        const body = await res.json()
        const data = body?.data as FeedResponse | undefined
        if (data) {
          unseenItems.value = data.items
          unseenCount.value = data.total
          return data.items
        }
      }
    } catch (e) {
      console.error('Erreur récupération des news non lues', e)
    }
    return []
  }

  async function markAllSeen(): Promise<void> {
    await withApiState(
      async () => {
        const res = await suiteStore.suiteFetch('news/seen-all', {
          method: 'PATCH',
          credentials: 'include',
        })
        if (res && typeof res !== 'boolean' && res.ok) {
          // Note : on ne touche pas à items[].readAt — l'indicateur "nouveau"
          // du fil reste visible pour la visite en cours.
          unseenCount.value = 0
          unseenItems.value = []
        }
      },
      { logLabel: 'Erreur lors du marquage des news comme vues' },
    )
  }

  /**
   * À l'arrivée dans l'app (montage du layout) : ouvre la modal "Quoi de
   * neuf ?" s'il existe des news publiées non vues. Remplace l'ancien
   * checkForNewVersion() basé sur localStorage.
   */
  async function checkForNews(): Promise<void> {
    const unseen = await fetchUnseen()
    if (unseen.length > 0) showNewsModal.value = true
  }

  function openNewsModal(): void {
    showNewsModal.value = true
  }

  /** Fermer la modal vaut accusé de lecture (comme l'ancienne modale version). */
  function closeNewsModal(): void {
    showNewsModal.value = false
    void markAllSeen()
  }

  /** Push WS reçu pendant une session active (toast géré par le WS store). */
  function onNewsPublished(): void {
    unseenCount.value += 1
  }

  return {
    items,
    total,
    isLoading,
    unseenItems,
    unseenCount,
    showNewsModal,

    fetchFeed,
    fetchUnseen,
    markAllSeen,
    checkForNews,
    openNewsModal,
    closeNewsModal,
    onNewsPublished,
  }
})
